import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Lock } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) {
      toast.success("Connecté");
      navigate("/");
    } else {
      setError(r.error);
      toast.error(r.error);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Full-page background */}
      <img
        src="https://images.pexels.com/photos/16176576/pexels-photo-16176576.jpeg?auto=compress&cs=tinysrgb&w=1920"
        alt="Concession automobile"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Dark gradient overlay so the form is readable */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/85 via-[#0a0a0a]/30 to-[#0a0a0a]/85" />

      {/* Left text overlay (hidden on small screens) */}
      <div className="hidden lg:flex absolute left-0 bottom-0 z-10 p-12 text-white max-w-xl flex-col gap-3">
        <div className="text-[11px] tracking-[0.25em] uppercase text-amber-400 font-bold">
          Plate-forme professionnelle
        </div>
        <h1 className="font-display font-black text-3xl xl:text-4xl tracking-tighter leading-[1.05]">
          Comptabilité client<br />& suivi paiements
        </h1>
        <div className="mt-3 flex items-start gap-3">
          <span className="block w-10 h-px bg-amber-400 mt-3 shrink-0" />
          <p className="text-sm text-slate-200 leading-relaxed">
            Suivez chaque vente, encaissement et créance — avec une vision claire par véhicule.
          </p>
        </div>
      </div>

      {/* Right login panel */}
      <div className="absolute inset-y-0 right-0 z-20 w-full md:w-[480px] lg:w-[520px] bg-[#0a0a0a]/92 backdrop-blur-xl border-l border-white/5 flex flex-col">
        <div className="flex-1 flex items-center justify-center px-8 sm:px-12 py-10">
          <div className="w-full max-w-sm">
            {/* Brand */}
            <div className="mb-10 text-center">
              <div className="font-display font-black tracking-tighter leading-none text-white text-2xl">
                GESTION CONCESSION
              </div>
              <div className="font-display font-black tracking-tighter leading-none text-amber-400 text-2xl mt-1">
                AUTOMOBILE
              </div>
              <div className="mt-4 inline-block px-3 py-1 text-[10px] tracking-[0.25em] uppercase text-amber-400 border border-amber-400/40 rounded-sm">
                Connexion
              </div>
            </div>

            <h2 className="font-display font-black text-2xl tracking-tighter text-white text-center mb-8">
              Accédez à votre espace
            </h2>

            <form onSubmit={onSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                  Email
                </Label>
                <div className="relative mt-2">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email"
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-500 rounded-sm h-11 focus-visible:ring-amber-400/50"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="password" className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                  Mot de passe
                </Label>
                <div className="relative mt-2">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password"
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-500 rounded-sm h-11 focus-visible:ring-amber-400/50"
                  />
                </div>
              </div>

              {error && (
                <div className="text-sm text-red-400" data-testid="login-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="w-full rounded-sm h-11 bg-amber-400 hover:bg-amber-300 text-[#0a0a0a] font-bold tracking-wide uppercase text-sm"
              >
                {loading ? "Connexion…" : "Se connecter"}
              </Button>

              <p className="text-xs text-slate-400 text-center pt-2">
                Pas encore de compte ?{" "}
                <Link to="/register" className="text-amber-400 hover:text-amber-300 font-semibold" data-testid="link-register">
                  Créer un compte
                </Link>
              </p>
            </form>
          </div>
        </div>

        <div className="px-8 sm:px-12 pb-6 text-center text-[10px] tracking-wider text-slate-500">
          © {new Date().getFullYear()} Gestion Concession Automobile — Tous droits réservés
        </div>
      </div>
    </div>
  );
}
